<script setup lang="ts">
import RatingBar from './RatingBar.vue'
import RatingDisplay from './RatingDisplay.vue'
import type { RatingsDistribution } from '~~/server/types/company'

interface Props {
  ratings_distribution: RatingsDistribution
  total_ratings: number
  overall_rating?: number
  leftPanelTitle?: string
  leftPanelContent?: string
  singlePanel?: boolean
}
const {
  ratings_distribution = (() => ({
    1: 0,
    2: 0,
    3: 0,
    4: 0,
    5: 0
  } as RatingsDistribution))(),
  total_ratings = 0,
  overall_rating = 0,
  leftPanelTitle = 'Overview',
  leftPanelContent = '',
  singlePanel = false
} = defineProps<Props>()
const totalRatings = computed(() => Math.max(0, total_ratings))
const distributionPercentages = computed(() => {
  const total = totalRatings.value
  if (total <= 0) return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  const result = {} as Record<number, number>;
  [5, 4, 3, 2, 1].forEach((rating) => {
    result[rating] = Math.round(
      (ratings_distribution[rating] / total) * 100
    )
  })
  return result
})
const formattedRating = computed(() => {
  return overall_rating ? overall_rating.toFixed(1) : null
})
const hasAnyRatings = computed(() => {
  return Object.values(ratings_distribution).some(count => count > 0)
})
</script>

<template>
  <div>
    <div
      v-if="totalRatings > 0 && hasAnyRatings"
      class="divide-y md:divide-y-0"
      :class="{ 'grid grid-cols-1 md:grid-cols-2 md:divide-x': !singlePanel }"
    >
      <div
        v-if="!singlePanel"
        class="p-6 bg-[#EBE6FF]"
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-xl font-semibold">
            {{ leftPanelTitle }}
          </h3>
        </div>
        <p class="text-gray-600 max-h-[150px] md:max-h-none overflow-y-auto pr-1">
          {{ leftPanelContent }}
        </p>
      </div>
      <div class="p-6 bg-[#F4F1FF]">
        <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
          <div class="mb-2 md:mb-0">
            <h3 class="text-xl font-semibold">
              Rating Distribution
            </h3>
            <p class="text-sm text-gray-600">
              {{ totalRatings }} rating{{ totalRatings === 1 ? '' : 's' }}
            </p>
          </div>
          <RatingDisplay
            :rating="overall_rating"
            show-label
          />
        </div>
        <div class="space-y-3">
          <RatingBar
            v-for="stars in [5, 4, 3, 2, 1]"
            :key="stars"
            :stars="stars"
            :percentage="distributionPercentages[stars]"
            :count="ratings_distribution[stars]"
          />
        </div>
      </div>
    </div>
    <div
      v-else
      class="p-6 text-center text-gray-500"
    >
      No ratings available
    </div>
  </div>
</template>
